/* =============================================================================
   nlp.js — behaviour for the "Improved Design" landing page (provetime-improved).
   Loaded only on that page. Vanilla, no deps. Reuses no globals.
   Components: hero carousel, story covers (open the shared lp-stories-viewer
   full-screen player), FAQ first-open, reviews swipe, global sticky CTA.
   ========================================================================== */
(function () {
  'use strict';

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }
  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // ---- Hero carousel (scroll-snap track + prev/next + thumbnails + counter) --
  function initHero() {
    document.querySelectorAll('[data-nlp-hero]').forEach(function (root) {
      var track = root.querySelector('[data-nlp-hero-track]');
      if (!track) return;
      var slides = Array.prototype.slice.call(track.children);
      var counter = root.querySelector('[data-nlp-hero-counter]');
      var prev = root.querySelector('[data-nlp-hero-prev]');
      var next = root.querySelector('[data-nlp-hero-next]');
      var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-nlp-hero-thumb]'));

      // promote lazy images (data-src -> src) once
      root.querySelectorAll('img[data-src]').forEach(function (img) {
        if (!img.getAttribute('src')) img.setAttribute('src', img.getAttribute('data-src'));
      });

      function current() {
        return track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
      }
      function go(i) {
        i = Math.max(0, Math.min(slides.length - 1, i));
        track.scrollTo({ left: i * track.clientWidth, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
      function update() {
        var i = current();
        if (counter) counter.textContent = i + 1 + ' / ' + slides.length;
        thumbs.forEach(function (t, ti) {
          var on = ti === i;
          t.setAttribute('aria-current', on ? 'true' : 'false');
          var ring = t.querySelector('[data-nlp-thumb-ring]');
          if (ring) ring.style.display = on ? 'block' : 'none';
        });
        // Keep the ACTIVE thumb visible: 5 thumbs fit per row, so from slide 6
        // onward the ring was scrolled out of view and the strip looked frozen.
        // Scroll the strip container directly (scrollIntoView could yank the
        // whole page vertically).
        var active = thumbs[i];
        var strip = active && active.parentElement;
        if (active && strip && strip.scrollWidth > strip.clientWidth) {
          var left = active.offsetLeft - strip.offsetLeft;
          var right = left + active.offsetWidth;
          var target = null;
          if (left < strip.scrollLeft) target = left - 8;
          else if (right > strip.scrollLeft + strip.clientWidth) target = right - strip.clientWidth + 8;
          if (target !== null) strip.scrollTo({ left: Math.max(0, target), behavior: reduceMotion() ? 'auto' : 'smooth' });
        }
      }
      // Wrap-around: hitting the end takes you back to the start instead of a
      // dead wall (owner report: slide 8 -> next did nothing).
      if (prev) prev.addEventListener('click', function () { var c = current(); go(c <= 0 ? slides.length - 1 : c - 1); });
      if (next) next.addEventListener('click', function () { var c = current(); go(c >= slides.length - 1 ? 0 : c + 1); });
      thumbs.forEach(function (t, ti) { t.addEventListener('click', function () { go(ti); }); });
      var raf;
      track.addEventListener('scroll', function () {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(update);
      }, { passive: true });
      update();
    });
  }

  // ---- Story covers: open the shared full-screen story player ----------------
  // Each story section renders snippets/lp-stories-viewer (same engine as the
  // live site). We hide its in-page circle row and trigger via the tall cover:
  // a delegated click looks up the viewer instance on its root and calls open().
  function initStoryCovers() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-nlp-cover]');
      if (!btn) return;
      e.preventDefault();
      var root = document.getElementById(btn.getAttribute('data-nlp-cover'));
      if (!root) return;
      if (root.__lpStories && typeof root.__lpStories.open === 'function') {
        root.__lpStories.open(0, btn);
        return;
      }
      // fallback: click the (hidden) circle trigger so the engine's own
      // delegation opens it, in case the instance isn't attached yet.
      var t = root.querySelector('[data-lp-story-open]');
      if (t) t.click();
    });
  }

  // ---- Story covers: blurred first-clip plays muted while in view -------------
  // Perf-safe: the covers sit below the hero, so nothing loads on initial paint
  // (preload="none" + src attached only once scrolled into view, paused when out
  // of view). Skipped entirely for reduced-motion / Save-Data / 2g — those keep
  // the static blurred poster. Initial-load speed (LCP) is never affected.
  function initStoryCoverVideos() {
    if (reduceMotion()) return;
    try { var c = navigator.connection; if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return; } catch (e) {}
    if (!('IntersectionObserver' in window)) return;
    var covers = document.querySelectorAll('.nlp-story-cover[data-nlp-cover-video]');
    if (!covers.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        var cover = en.target;
        var v = cover.querySelector('.nlp-story-cover__video');
        if (!v) return;
        if (en.isIntersecting) {
          if (!v.getAttribute('src')) {
            v.setAttribute('src', cover.getAttribute('data-nlp-cover-video'));
            try { v.load(); } catch (e) {}
          }
          var p = v.play();
          if (p && p.then) p.then(function () { cover.classList.add('is-playing'); }).catch(function () {});
          else cover.classList.add('is-playing');
        } else {
          try { v.pause(); } catch (e) {}
        }
      });
    }, { threshold: 0.4 });
    covers.forEach(function (c) { io.observe(c); });
  }

  // ---- FAQ: open the first item on load (never driven by scroll) -------------
  function initFaq() {
    document.querySelectorAll('[data-nlp-faq]').forEach(function (root) {
      var first = root.querySelector('details.faq');
      if (first) first.setAttribute('open', '');
    });
  }

  // ---- Reviews: arrows scroll the swipe carousel (mobile) --------------------
  function initReviews() {
    document.querySelectorAll('[data-nlp-reviews]').forEach(function (root) {
      var cards = root.querySelector('.s7-cards');
      if (!cards) return;
      function step(dir) {
        var card = cards.querySelector('.s7-card');
        var w = card ? card.offsetWidth + 14 : cards.clientWidth * 0.86;
        cards.scrollBy({ left: dir * w, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
      var prev = root.querySelector('[data-nlp-reviews-prev]');
      var next = root.querySelector('[data-nlp-reviews-next]');
      if (prev) prev.addEventListener('click', function () { step(-1); });
      if (next) next.addEventListener('click', function () { step(1); });
    });
  }

  // ---- Sticky CTA: show after hero CTA leaves; hide on embed/modal -----------
  function initSticky() {
    var wrap = document.querySelector('.sticky-cta-wrap');
    if (!wrap) return;
    var heroCta = document.querySelector('[data-nlp-hero-cta]');
    var embed = document.querySelector('#nlp-booking-embed');
    var heroPast = !heroCta; // if no sentinel, treat as already past
    var embedVisible = false;

    function modalOpen() { return !!document.querySelector('.nob-modal:not([hidden])'); }
    function update() {
      var show = heroPast && !embedVisible && !modalOpen();
      wrap.style.transform = show ? 'translateY(0)' : 'translateY(160%)';
      wrap.style.opacity = show ? '1' : '0';
      wrap.style.pointerEvents = show ? 'auto' : 'none';
    }
    if ('IntersectionObserver' in window) {
      if (heroCta) {
        // Show only once the hero CTA has scrolled ABOVE the viewport (top < 0).
        // On mobile the hero media stacks on top, so the CTA starts BELOW the fold
        // (not intersecting, top > 0) — without the top check it would show on load.
        new IntersectionObserver(function (es) { heroPast = !es[0].isIntersecting && es[0].boundingClientRect.top < 0; update(); }, { threshold: 0 }).observe(heroCta);
      }
      if (embed) {
        new IntersectionObserver(function (es) { embedVisible = es[0].isIntersecting; update(); }, { threshold: 0.2 }).observe(embed);
      }
      var modal = document.querySelector('.nob-modal');
      if (modal && 'MutationObserver' in window) {
        new MutationObserver(update).observe(modal, { attributes: true, attributeFilter: ['hidden'] });
      }
    } else {
      heroPast = true;
    }
    update();
  }

  // ---- Hero lightbox: trykk på sliden for å se den i full størrelse ---------
  // Slidene er infografikk, og på mobil er karusellen ~327 px = 20 % av bildet.
  // Overskrift og tall leser fint der, men kildelinja og forbeholdene gjør det
  // ikke. Full visning + zoom gjør at det som KVALIFISERER påstanden faktisk kan
  // leses — like mye et etterrettelighetskrav som en UX-forbedring.
  function initHeroLightbox() {
    var box = null, imgEl = null, vidEl = null, capEl = null, zoomBtn = null, wrap = null;
    var items = [], idx = 0, zoomed = false, lastFocus = null;

    function btnCss() {
      return 'display:inline-flex; align-items:center; gap:6px; background:rgba(251,247,240,.14);' +
        'color:#FBF7F0; border:1px solid rgba(251,247,240,.32); border-radius:999px;' +
        'padding:9px 15px; font-family:sofia-pro,-apple-system,BlinkMacSystemFont,sans-serif;' +
        'font-size:14px; font-weight:600; line-height:1; cursor:pointer;';
    }

    function build() {
      if (box) return;
      box = document.createElement('div');
      box.setAttribute('role', 'dialog');
      box.setAttribute('aria-modal', 'true');
      box.setAttribute('aria-label', 'Bildet i full størrelse');
      box.style.cssText = 'position:fixed; inset:0; z-index:1200; display:none;' +
        'background:rgba(26,17,15,.94); flex-direction:column;';

      var bar = document.createElement('div');
      bar.style.cssText = 'flex:0 0 auto; display:flex; align-items:center;' +
        'justify-content:space-between; gap:12px; color:#FBF7F0;' +
        'padding:max(10px, env(safe-area-inset-top)) 12px 10px;';

      capEl = document.createElement('span');
      capEl.style.cssText = 'font-family:sofia-pro,-apple-system,sans-serif; font-size:13px;' +
        'opacity:.75; font-variant-numeric:tabular-nums;';

      zoomBtn = document.createElement('button');
      zoomBtn.type = 'button';
      zoomBtn.style.cssText = btnCss();

      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.setAttribute('data-nlp-lb-close', '');
      closeBtn.innerHTML = '<span aria-hidden="true" style="font-size:19px;line-height:1;">&times;</span> Lukk';
      closeBtn.style.cssText = btnCss();

      var right = document.createElement('div');
      right.style.cssText = 'display:flex; gap:8px;';
      right.appendChild(zoomBtn);
      right.appendChild(closeBtn);
      bar.appendChild(capEl);
      bar.appendChild(right);

      wrap = document.createElement('div');
      wrap.style.cssText = 'flex:1 1 auto; overflow:auto; display:flex; align-items:center;' +
        'justify-content:center; -webkit-overflow-scrolling:touch;' +
        'padding:0 10px max(14px, env(safe-area-inset-bottom));';
      imgEl = document.createElement('img');
      imgEl.alt = '';
      wrap.appendChild(imgEl);

      vidEl = document.createElement('video');
      vidEl.setAttribute('playsinline', '');
      // Ingen kontroller: videoen er en stemningsloop fra senteret, ikke noe
      // man skal spole i eller skru opp lyden på. Ren flate.
      vidEl.muted = true;          // uten dette nekter mobil å autospille
      vidEl.loop = true;
      vidEl.setAttribute('disablepictureinpicture', '');
      vidEl.style.display = 'none';
      vidEl.style.pointerEvents = 'none';   // ingen native meny ved langtrykk
      wrap.appendChild(vidEl);

      // Pilene ligger PÅ bildet, ikke nederst på skjermen: nederste stripe
      // spises av nettleserens egen verktøylinje på mobil (og av Shopifys
      // preview-bar), så knapper der er utilgjengelige akkurat når man trenger dem.
      function arrow(sym, label, side) {
        var b = document.createElement('button');
        b.type = 'button';
        b.setAttribute('aria-label', label);
        b.textContent = sym;
        b.style.cssText = 'position:absolute; ' + side + ':10px; top:50%; transform:translateY(-50%);' +
          'z-index:2; width:46px; height:46px; border-radius:50%; border:1px solid rgba(251,247,240,.3);' +
          'background:rgba(26,17,15,.55); color:#FBF7F0; font-size:24px; line-height:1;' +
          'display:flex; align-items:center; justify-content:center; cursor:pointer;';
        return b;
      }
      var pv = arrow('‹', 'Forrige bilde', 'left');
      var nx = arrow('›', 'Neste bilde', 'right');

      box.appendChild(bar);
      box.appendChild(wrap);
      // Pilene henges på den FASTE beholderen. Lå de på wrap, fulgte de med
      // når man scrollet et zoomet bilde og havnet midt oppi motivet.
      box.appendChild(pv);
      box.appendChild(nx);
      document.body.appendChild(box);

      closeBtn.addEventListener('click', close);
      zoomBtn.addEventListener('click', function () { setZoom(!zoomed); });
      imgEl.addEventListener('click', function () { setZoom(!zoomed); });
      pv.addEventListener('click', function () { show(idx - 1); });
      nx.addEventListener('click', function () { show(idx + 1); });
      box.addEventListener('click', function (e) {
        if (e.target === box || e.target === wrap) close();   // pilene stopper selv via egne handlere
      });
      document.addEventListener('keydown', function (e) {
        if (!box || box.style.display === 'none') return;
        if (e.key === 'Escape') { close(); return; }
        if (e.key === 'ArrowLeft') { show(idx - 1); return; }
        if (e.key === 'ArrowRight') { show(idx + 1); return; }
        if (e.key === 'Tab') {
          var f = box.querySelectorAll('button');
          if (!f.length) return;
          var first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
    }

    function setZoom(on) {
      zoomed = on;
      wrap.style.alignItems = on ? 'flex-start' : 'center';
      imgEl.style.cssText = on
        ? 'width:min(1600px, 210vw); max-width:none; height:auto; display:block; cursor:zoom-out; margin:auto 0;'
        : 'max-width:100%; max-height:100%; width:auto; height:auto; display:block; cursor:zoom-in;';
      zoomBtn.textContent = on ? 'Tilpass skjermen' : 'Forstørr';
      wrap.scrollTop = 0;
      wrap.scrollLeft = 0;   // lesing starter øverst til venstre, ikke midt i bildet
    }

    function show(i) {
      if (!items.length) return;
      idx = (i + items.length) % items.length;
      var it = items[idx];
      if (vidEl) vidEl.pause();
      if (it.video) {
        imgEl.style.display = 'none';
        vidEl.style.display = 'block';
        vidEl.style.maxWidth = '100%';
        vidEl.style.maxHeight = '100%';
        if (it.poster) vidEl.poster = it.poster;
        if (vidEl.getAttribute('src') !== it.src) vidEl.setAttribute('src', it.src);
        var pr = vidEl.play(); if (pr && pr.catch) pr.catch(function () {});
        zoomBtn.style.display = 'none';          // zoom gir ingen mening på video
      } else {
        vidEl.style.display = 'none';
        imgEl.style.display = 'block';
        imgEl.src = it.src;
        imgEl.alt = it.alt || '';
        zoomBtn.style.display = '';
        setZoom(false);
      }
      // Teller på slidens plass i KARUSELLEN, så «1 av 9» her matcher «1 / 9» der.
      capEl.textContent = (it.slide + 1) + ' av ' + (it.total || items.length);
    }

    function open(list, i) {
      build();
      items = list;
      lastFocus = document.activeElement;
      box.style.display = 'flex';
      document.documentElement.style.overflow = 'hidden';
      show(i);
      var c = box.querySelector('[data-nlp-lb-close]');
      if (c) c.focus();
    }

    function close() {
      if (!box) return;
      if (vidEl) vidEl.pause();
      box.style.display = 'none';
      document.documentElement.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    document.querySelectorAll('[data-nlp-hero]').forEach(function (root) {
      var track = root.querySelector('[data-nlp-hero-track]');
      if (!track) return;
      var slides = Array.prototype.slice.call(track.children);

      function list() {
        var out = [];
        slides.forEach(function (s, si) {
          var im = s.querySelector('img');
          if (im) { out.push({ src: im.currentSrc || im.src, alt: im.alt, slide: si }); return; }
          // Videoen er slide 1 — den ALLE ser først. Lot vi den være uklikkbar,
          // lærte folk at «trykk gjør ingenting» og prøvde aldri på de andre.
          var vi = s.querySelector('video');
          if (vi) out.push({ src: vi.currentSrc || vi.src, poster: vi.poster,
                             video: true, alt: 'Video fra senteret', slide: si });
        });
        return out;
      }

      function openHere() {
        var all = list();
        if (!all.length) return;
        var cur = track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
        var at = 0;
        all.forEach(function (o, oi) { if (o.slide <= cur) at = oi; });
        open(all, at);
      }

      // Ingen egen knapp: eieren vil at selve bildet skal åpne visningen, og en
      // knapp oppå en ferdig designet infografikk roter til komposisjonen.
      // Desktop får zoom-in-markøren som affordance; på touch er trykk-for-å-
      // forstørre en innarbeidet gest.
      track.style.cursor = 'zoom-in';


      // Trykk på selve bildet — men et SVEIP skal aldri regnes som et trykk.
      var sx = 0, sy = 0, st = 0;
      track.addEventListener('pointerdown', function (e) {
        sx = e.clientX; sy = e.clientY; st = Date.now();
      }, { passive: true });
      track.addEventListener('pointerup', function (e) {
        if (e.target.closest && e.target.closest('button, a')) return;
        var moved = Math.abs(e.clientX - sx) + Math.abs(e.clientY - sy);
        if (moved < 12 && Date.now() - st < 500) openHere();
      });
    });
  }

  ready(function () {
    try { initHero(); } catch (e) {}
    try { initHeroLightbox(); } catch (e) {}
    try { initStoryCovers(); } catch (e) {}
    try { initStoryCoverVideos(); } catch (e) {}
    try { initFaq(); } catch (e) {}
    try { initReviews(); } catch (e) {}
    try { initSticky(); } catch (e) {}
  });
})();
